import React, { useEffect, useState } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { AxisDomain } from "recharts/types/util/types";
import { TextField, MenuItem, Container, Grid } from "@mui/material";
import moment from "moment";
import _ from "lodash";

import { GridCard, StyledGridHeader } from "../StyledComponents";
import { AllMissionDataType } from "../types/MissionData";
import { GameLog } from "../types/GameLog";
import {
  countPlaytime,
  formatMinigameData,
  formatMahjongData,
  formatMinigameData1,
  formatMahjongData1,
  formatCalligraphyPlaytimeData,
} from "../pages/NewReportPage";
import { CalligraphyRecordEntry } from "../types/CalligraphyData";
import { useTranslation } from "react-i18next";
import YearMenuItems from "./YearMenuItems";

const units = ["day", "week", "month", "all"] as const;
type Unit = (typeof units)[number];

export const months = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12] as const;
export type Month = (typeof months)[number];

interface PlaytimeTabProps {
  missionData: AllMissionDataType[];
  mahjongData: GameLog[] | undefined;
  calligraphyData: CalligraphyRecordEntry[] | undefined;
}

interface PlaytimeChartData {
  date: number;
  minigame: number;
  mahjong: number;
  calligraphy: number;
}

export default function PlaytimeTab(props: PlaytimeTabProps) {
  const { t } = useTranslation();
  const [playtime, setPlaytime] = useState<PlaytimeChartData[]>();
  const [playtimeChartData, setPlaytimeChartData] =
    useState<PlaytimeChartData[]>();

  const [timeUnit, setTimeUnit] = useState<{ year: number; month: number }>({
    year: parseInt(moment().format("YYYY")),
    month: parseInt(moment().format("MM")),
  });

  const [formatedMahjongPlaytime, setFormatedMahjongPlaytime] =
    useState<{ date: number; playtime: number }[]>();
  const [formatedMissionPlaytime, setFormatedMissionPlaytime] =
    useState<{ date: number; playtime: number }[]>();
  const [formatedCalligraphyPlaytime, setFormatedCalligraphyPlaytime] =
    useState<{ date: number; playtime: number }[]>();

  const [customTicks, setCustomTicks] = useState<number[]>([]);
  const [customDomain, setCustomDomain] = useState<[AxisDomain, AxisDomain]>();

  const translationMonth = {
    1: t("months.1"),
    2: t("months.2"),
    3: t("months.3"),
    4: t("months.4"),
    5: t("months.5"),
    6: t("months.6"),
    7: t("months.7"),
    8: t("months.8"),
    9: t("months.9"),
    10: t("months.10"),
    11: t("months.11"),
    12: t("months.12"),
  };

  const translationUnit = {
    day: t("date_noun.lastWeek"),
    week: t("date_noun.month"),
    month: t("date_noun.year"),
    all: t("date_noun.all"),
  };

  useEffect(() => {
    if (playtime) {
      setPlaytimeChartData(extractDataByTime(playtime));
    }

    setCustomTicks(generateTick());
    setCustomDomain(computeCustomDomain());
  }, [timeUnit]);

  useEffect(() => {
    if (props.missionData)
      setFormatedMissionPlaytime(processMissionData(props.missionData));
  }, [props.missionData]);

  useEffect(() => {
    if (props.mahjongData)
      setFormatedMahjongPlaytime(processMahjongData(props.mahjongData));
  }, [props.mahjongData]);

  useEffect(() => {
    if (props.calligraphyData)
      setFormatedCalligraphyPlaytime(
        processCalligraphyData(props.calligraphyData)
      );
  }, [props.calligraphyData]);

  useEffect(() => {
    if (
      formatedMahjongPlaytime !== undefined &&
      formatedMissionPlaytime !== undefined &&
      formatedCalligraphyPlaytime !== undefined
    ) {
      setPlaytime(combinePlaytimeData());
    }
  }, [
    formatedMahjongPlaytime,
    formatedMissionPlaytime,
    formatedCalligraphyPlaytime,
  ]);

  useEffect(() => {
    setTimeUnit({
      year: parseInt(moment().format("YYYY")),
      month: parseInt(moment().format("MM")),
    });
  }, [playtime]);

  useEffect(() => {}, [playtimeChartData]);

  const processMissionData = (missionData: AllMissionDataType[]) => {
    // console.log(countPlaytime(formatMinigameData1(missionData)));
    const tempMinigame = countPlaytime(formatMinigameData(missionData)).map(
      (x) => {
        return {
          date: moment(x.date).valueOf(),
          playtime: Math.round(x.playtime / 60 / 10) / 100,
        };
      }
    );

    return tempMinigame.sort((a, b) => b.date - a.date);
  };

  const processMahjongData = (mahjongData: GameLog[]) => {
    const tempMahjong = countPlaytime(formatMahjongData(mahjongData)).map(
      (x) => {
        return {
          date: moment(x.date).valueOf(),
          playtime: Math.round(x.playtime / 60 / 10) / 100,
        };
      }
    );

    return tempMahjong.sort((a, b) => b.date - a.date);
  };

  const processCalligraphyData = (
    calligraphyData: CalligraphyRecordEntry[]
  ) => {
    const tempCalligraphy = countPlaytime(
      formatCalligraphyPlaytimeData(calligraphyData)
    ).map((x) => {
      return {
        date: moment(x.date).valueOf(),
        playtime: Math.round((x.playtime / 60) * 100) / 100,
      };
    });

    return tempCalligraphy.sort((a, b) => b.date - a.date);
  };

  const combinePlaytimeData = () => {
    // Minigame iteration
    let playTimeBuffer = formatedMissionPlaytime!.reduce<{
      [key: string]: PlaytimeChartData;
    }>((buffer, entry) => {
      buffer[entry.date] ||
        (buffer[entry.date] = {
          date: entry.date,
          minigame: 0,
          mahjong: 0,
          calligraphy: 0,
        });
      buffer[entry.date].minigame = Math.round(entry.playtime);
      return buffer;
    }, {});

    // Mahjong iteration
    playTimeBuffer = formatedMahjongPlaytime!.reduce<{
      [key: string]: PlaytimeChartData;
    }>((buffer, entry) => {
      buffer[entry.date] ||
        (buffer[entry.date] = {
          date: entry.date,
          minigame: 0,
          mahjong: 0,
          calligraphy: 0,
        });
      buffer[entry.date].mahjong = Math.round(entry.playtime);
      return buffer;
    }, playTimeBuffer);

    // Calligraphy iteration
    playTimeBuffer = formatedCalligraphyPlaytime!.reduce<{
      [key: string]: PlaytimeChartData;
    }>((buffer, entry) => {
      buffer[entry.date] ||
        (buffer[entry.date] = {
          date: entry.date,
          minigame: 0,
          mahjong: 0,
          calligraphy: 0,
        });
      buffer[entry.date].calligraphy = Math.round(entry.playtime);
      return buffer;
    }, playTimeBuffer);

    return Object.values(playTimeBuffer);
  };

  const extractDataByTime = (
    data: {
      date: number;
      minigame: number;
      mahjong: number;
      calligraphy: number;
    }[]
  ) => {
    if (!timeUnit) return;

    let dataInRange: {
      date: number;
      minigame: number;
      mahjong: number;
      calligraphy: number;
    }[];

    if (timeUnit.month === -1) {
      const startOfYear = moment(timeUnit!.year.toString())
        .startOf("year")
        .valueOf();
      const endOfYear = moment(timeUnit!.year.toString())
        .endOf("year")
        .valueOf();
      dataInRange = data.filter(
        (entry) => entry.date < endOfYear && entry.date > startOfYear
      );

      // Group data to month
      const groupedData = _(dataInRange)
        .map((d) => {
          return { ...d, date: moment(d.date).startOf("month").valueOf() };
        })
        .groupBy((d) => d.date)
        .valueOf() as {
        [key: number]: {
          date: number;
          minigame: number;
          mahjong: number;
          calligraphy: number;
        }[];
      };

      // console.log(groupedData);

      dataInRange = Object.entries(groupedData).map(
        ([monthTimestamp, list]) => {
          return {
            date: parseInt(monthTimestamp),
            mahjong: _(list).sumBy((d) => d.mahjong),
            minigame: _(list).sumBy((d) => d.minigame),
            calligraphy: _(list).sumBy((d) => d.calligraphy),
          };
        }
      );

      const dummyFiller = _.range(1, 13).map((month) => {
        const currentMonth = moment(timeUnit!.year + "-" + month).valueOf();
        if (!dataInRange.find((d) => d.date === currentMonth))
          dataInRange.push({
            date: currentMonth,
            minigame: 0,
            mahjong: 0,
            calligraphy: 0,
          });
      });
    } else {
      const startOfMonth = moment(timeUnit!.year + "-" + timeUnit!.month)
        .startOf("month")
        .valueOf();
      const endOfMonth = moment(timeUnit!.year + "-" + timeUnit!.month)
        .endOf("month")
        .valueOf();

      dataInRange = data.filter(
        (entry) => entry.date < endOfMonth && entry.date > startOfMonth
      );

      // Fill 0s
      const dummyFiller = _.range(
        1,
        moment(timeUnit!.year + "-" + timeUnit!.month).daysInMonth() + 1
      ).forEach((day) => {
        const currentDate = moment(
          timeUnit!.year + "-" + timeUnit!.month + "-" + day
        ).valueOf();
        if (!dataInRange.find((d) => d.date === currentDate))
          dataInRange.push({
            date: currentDate,
            minigame: 0,
            mahjong: 0,
            calligraphy: 0,
          });
      });
    }

    const final = dataInRange.sort((a, b) => a.date - b.date);
    return final;
  };

  const computeCustomDomain = (): [AxisDomain, AxisDomain] => {
    return timeUnit.month !== -1
      ? [
          moment(
            (timeUnit.year
              ? timeUnit.year.toString()
              : moment().format("YYYY")) +
              "-" +
              timeUnit.month
          )
            .startOf("month")
            .valueOf() as unknown as AxisDomain,
          moment(
            (timeUnit.year
              ? timeUnit.year.toString()
              : moment().format("YYYY")) +
              "-" +
              timeUnit.month
          )
            .endOf("month")
            .valueOf() as unknown as AxisDomain,
        ]
      : [
          moment(
            timeUnit.year ? timeUnit.year.toString() : moment().format("YYYY")
          )
            .startOf("year")
            .valueOf() as unknown as AxisDomain,
          moment(
            timeUnit.year ? timeUnit.year.toString() : moment().format("YYYY")
          )
            .endOf("year")
            .valueOf() as unknown as AxisDomain,
        ];
  };

  const generateTick = () => {
    const start: moment.Moment = moment(
      (timeUnit.year ? timeUnit.year.toString() : moment().format("YYYY")) +
        (timeUnit.month !== -1 ? `-${timeUnit.month}` : "")
    );
    const resultArray = [];

    if (timeUnit.month === -1) {
      for (
        const end = start.clone().add(1, "year");
        start.isBefore(end);
        start.add(1, "month")
      ) {
        resultArray.push(start.valueOf());
      }
    } else {
      for (
        const end = start.clone().add(1, "month");
        start.isBefore(end);
        start.add(1, "day")
      ) {
        resultArray.push(start.valueOf());
      }
    }

    return resultArray;
  };

  return (
    <Container style={{ minHeight: "100px", paddingBottom: 20, marginTop: 40 }}>
      <Grid container spacing={4} id="info">
        <Grid item md={12} sm={12} xs={12}>
          <GridCard
            title={t("playtimeTab.title_mode_playtime")}
            desc={t("playtimeTab.desc_mode_playtime")}
          >
            <Grid container>
              <Grid item>
                <TextField
                  id="year"
                  select
                  label={t("units_date.year")}
                  value={timeUnit?.year}
                  onChange={(e) =>
                    setTimeUnit({
                      year: parseInt(e.target.value),
                      month: timeUnit!.month,
                    })
                  }
                  margin="normal"
                  style={{
                    width: 200,
                    justifySelf: "flex-start",
                    display: "flex",
                    margin: 40,
                  }}
                >
                  {YearMenuItems()}
                </TextField>
              </Grid>
              <Grid item>
                <TextField
                  id="month"
                  select
                  label={t("units_date.month")}
                  value={timeUnit?.month}
                  onChange={(e) =>
                    setTimeUnit({
                      year: timeUnit!.year,
                      month: parseInt(e.target.value) as Month,
                    })
                  }
                  margin="normal"
                  style={{
                    width: 200,
                    justifySelf: "flex-start",
                    display: "flex",
                    margin: 40,
                  }}
                >
                  <MenuItem key={-1} value={-1}>
                    {t("months.-1")}
                  </MenuItem>
                  {months.map((option) => (
                    <MenuItem key={option} value={option}>
                      {translationMonth[option]}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            </Grid>

            {timeUnit !== undefined &&
            playtime !== undefined &&
            playtime.length !== 0 ? (
              <ResponsiveContainer width="100%" height={288}>
                <LineChart
                  width={1}
                  height={250}
                  margin={{
                    top: 15,
                    right: 30,
                    left: 10,
                    bottom: 60,
                  }}
                  data={playtimeChartData}
                >
                  <CartesianGrid />
                  <XAxis
                    type="number"
                    interval={0}
                    dataKey={"date"}
                    name="Time"
                    domain={customDomain as AxisDomain}
                    tickFormatter={(unixTime) =>
                      timeUnit?.month !== -1
                        ? moment(unixTime).format("MMM Do")
                        : moment(unixTime).format("YYYY MMM")
                    }
                    ticks={customTicks}
                    angle={-35}
                    textAnchor="end"
                  />
                  <YAxis />
                  <Tooltip
                    labelFormatter={(value) =>
                      moment(value).format(
                        timeUnit?.month !== -1 ? "YYYY MMM Do" : "YYYY-MM"
                      )
                    }
                    cursor={{ strokeDasharray: "3 3" }}
                    formatter={(value: string) => {
                      return value + t("units_date.minute");
                    }}
                  />
                  <Legend
                    verticalAlign="top"
                    wrapperStyle={{ lineHeight: "40px" }}
                  />
                  <Line
                    name={t("game_key.minigame")}
                    type="monotone"
                    dataKey={"minigame"}
                    stroke="#1f4785"
                    dot={true}
                  />
                  <Line
                    name={t("game_key.mahjong")}
                    type="monotone"
                    dataKey={"mahjong"}
                    stroke="#19b521"
                    dot={true}
                  />
                  <Line
                    name={t("game_key.calligraphy")}
                    type="monotone"
                    dataKey={"calligraphy"}
                    stroke="#ff6188"
                    dot={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              t("no__.game_record")
            )}
          </GridCard>
        </Grid>
      </Grid>
    </Container>
  );
}
